import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

const fetcher = (u) => api.get(u).then((r) => r.data);
const money = (v) => Math.abs(v).toLocaleString("fr-FR", { maximumFractionDigits: 2 });

function ScoreBar({ score, testid }) {
  const color = score >= 85 ? "bg-emerald-500" : score >= 65 ? "bg-amber-500" : "bg-zinc-300";
  const text = score >= 85 ? "text-emerald-700" : score >= 65 ? "text-amber-700" : "text-zinc-500";
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="w-20 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-xs font-semibold ${text}`}>{Math.round(score)}%</span>
    </div>
  );
}

export default function BankMatching({ file }) {
  const key = `/accounting/files/${file.id}/bank-matching/suggestions`;
  const { data } = useSWR(key, fetcher);
  const [busy, setBusy] = React.useState(null);

  const apply = async (brouillardId, entryId) => {
    setBusy(entryId);
    try {
      const r = await api.post(`/accounting/files/${file.id}/bank-matching/apply`, {
        brouillard_id: brouillardId, entry_id: entryId,
      });
      toast.success(`Rapprochement appliqué — lettrage ${r.data.code}`);
      mutate(key);
      mutate(`/accounting/files/${file.id}/entries`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Rapprochement impossible"); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="text-sm text-zinc-500">Chargement…</div>;
  if (data.supported === false) {
    return (
      <div className="text-sm text-zinc-500 border border-zinc-200 rounded-md p-6 bg-white" data-testid="bm-unsupported">
        Le rapprochement assisté est disponible pour les dossiers SYSCOHADA (comptes d'attente 4711/4712).
      </div>
    );
  }

  const rows = data.rows || [];
  return (
    <div className="space-y-4" data-testid="bankmatching-panel">
      <div className="text-xs text-zinc-500 max-w-3xl">
        Suggestions automatiques entre les brouillards bancaires (comptes d'attente 4711/4712, imports CSV ou Powens) et les
        écritures clients/fournisseurs ouvertes. Le score combine montant, libellé et date. « Lettrer » crée l'extourne en OD,
        lettre la facture et valide le brouillard en un clic.
      </div>
      {rows.length === 0 && (
        <div className="border border-zinc-200 rounded-md p-8 bg-white text-center text-sm text-zinc-500" data-testid="bm-empty">
          <CheckCircle size={28} className="mx-auto mb-2 text-emerald-500" />
          Aucun brouillard bancaire en attente de rapprochement.
        </div>
      )}
      {rows.map((r) => (
        <div key={r.brouillard.entry_id} className="border border-zinc-200 rounded-md bg-white p-4" data-testid={`bm-row-${r.brouillard.entry_id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <div className="min-w-0">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#2E3192] font-semibold">
                {r.brouillard.direction === "encaissement" ? "Encaissement" : "Décaissement"} · {r.brouillard.piece}
              </span>
              <div className="text-sm font-medium mt-0.5 truncate">{r.brouillard.label}</div>
              <div className="text-xs text-zinc-500">{r.brouillard.date}</div>
            </div>
            <div className={`font-mono text-sm font-semibold ${r.brouillard.direction === "encaissement" ? "text-emerald-700" : "text-rose-700"}`}>
              {r.brouillard.direction === "encaissement" ? "+" : "−"}{money(r.brouillard.amount)} {file.currency}
            </div>
          </div>
          {r.suggestions.length === 0 ? (
            <div className="text-xs text-zinc-400 italic">Aucune écriture ouverte correspondante — à analyser manuellement.</div>
          ) : (
            <div className="space-y-2">
              {r.suggestions.map((s) => (
                <div key={s.entry_id} className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 pt-2" data-testid={`bm-suggestion-${s.entry_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <ArrowRight size={14} className="text-zinc-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm truncate">{s.label}</div>
                      <div className="text-xs text-zinc-500">
                        {s.date} · {s.piece} · {money(s.amount)} {file.currency}
                        {!s.exact && <span className="ml-1 text-amber-600">(montant différent — à traiter manuellement)</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ScoreBar score={s.score} testid={`bm-score-${s.entry_id}`} />
                    <Button size="sm" disabled={!s.exact || busy === s.entry_id}
                      onClick={() => apply(r.brouillard.entry_id, s.entry_id)}
                      className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid={`bm-apply-${s.entry_id}`}>
                      {busy === s.entry_id ? "…" : "Lettrer"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
